import {
  addDistinct,
  convertToTitleCase,
  filterEmpty,
  FluidContextMenuItemType,
  FluidContextMenuSection,
  FluidContextMenuToggle,
  FluidCustomFilterFn,
  FluidCustomFilterSet,
  FluidFilter,
  FluidFilterSet,
  FluidFilterType,
  FluidRowData,
  FluidTableGroupedDataset,
  FluidTableHeader,
  FluidTableRowData,
  getAtPath,
  normalise,
  sortAlpha,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import { FluidTableTranslationOptions } from '@lmig/fluid-core/lib/i18n/types/fluid-table-translation-options.type';
import { isWithinInterval, parseISO } from 'date-fns';

import {
  DATE_RANGE_SLOT_SUFFIX,
  FILTERED,
  FORM_EXPANDED,
  ID,
  SEARCH_SLOT_SUFFIX,
  TABLE_FILTER_STATE,
  VISIBLE,
} from '../../../../constants/constants';
import { FluidI18nStore } from '../../../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { FluidBrowserStorage } from '../../../../utils/store/local-storage.class';
import { StringReturn, Utils } from '../../../../utils/utils';
import { sendEvent } from '../../../fluid-data-layer/fluid-data-layer.utils';
import { FluidTable } from '../../fluid-table';
import { groupDataSource } from '../fluid-table-grouping.utils';
import { applyPagination } from '../fluid-table-pagination.utils';
import {
  BLANK_FILTER_ID_VALUE,
  BLANK_FILTER_LABEL,
  COLUMN_FILTER_TITLE_DEFAULT,
  DatasourceItem,
  DEFAULT_COMPACT_SECTION_HEIGHT,
  FILTER_SET_ID,
  FILTER_TOGGLE_ID,
  FILTER_TYPE_DELIM,
  FilterSetValue,
} from './fluid-filter.constants';
import { FILTER_SET } from './fluid-filter-types';

export type FilterEventPayload = {
  appliedFilters: Record<string, any>;
  filteredRows: FluidRowData[];
};

/**
 * Collects the table filters from _selectedFilters into groups of column filters (derived values)
 * and custom filters (set by developer), processes to find all data items that pass the predicates
 * before updating the _filteredRows with those that don't pass.
 * @param host
 */
export async function processTableFilters(host: Partial<FluidTable>) {
  const start = performance.now();
  const { serverMode } = host;
  const filterExtractionStart = performance.now();

  const dateRangeFilters: FluidFilterSet[] = collectDateRangeFilters(
    host._rangeFilter
  );
  Utils.debug('[TABLE] Date Range Filters', { dateRangeFilters });
  const columnFilters: FluidFilterSet[] = extractFiltersByType(
    host._selectedFilters,
    FluidFilterType.COLUMN
  );
  const customFilters: FluidFilterSet[] = extractFiltersByType(
    host._selectedFilters,
    FluidFilterType.CUSTOM
  );
  const searchFilters: FluidFilterSet[] = extractFiltersByType(
    host._selectedFilters,
    FluidFilterType.CONTAINS
  );
  Utils.logPerformance('filterExtraction', filterExtractionStart);
  Utils.debug(
    '[TABLE] Processing table filters',
    dateRangeFilters,
    columnFilters,
    customFilters,
    searchFilters
  );

  if (!serverMode) {
    const startRun = performance.now();
    const passingDataItems = runFilters(
      columnFilters,
      customFilters,
      searchFilters,
      dateRangeFilters,
      host._dataSource
    );
    Utils.logPerformance('runFilters', startRun);
    setFilteredRows(host, passingDataItems);
    Utils.debug('[TABLE] Passing Data items', passingDataItems);
  }
  Utils.logPerformance('processTableFilters', start);
  refreshTableAfterFilters(host);
  persistFilterState(host);
}

/**
 * Extracts date range filters from the rangeFilter memory store to pull
 * out just the date information and data path.
 *
 * Hydrates appliedFiltersByColumn with data via function composition.
 *
 * @param appliedFiltersByColumn
 * @param rangeFilters
 */
export function collectRangeFiltersByColumn(
  appliedFiltersByColumn: Record<string, any>,
  rangeFilters: Record<string, any>
) {
  return Object.keys(rangeFilters).reduce(
    (appliedFilters: Record<string, any>, dataPath: string) => {
      return {
        ...appliedFilters,
        [dataPath]: rangeFilters[dataPath],
      };
    },
    appliedFiltersByColumn
  );
}

/**
 * Creates a simplified array of filter IDs per data path that are currently
 * applied, so that we can use that to determine filter state on load (if applicable).
 *
 * @param filterRecord
 */
export function collectFilterIdsFromRecord(
  filterRecord: Record<string, FluidFilterSet[]>
): Record<string, string[]> {
  return Object.keys(filterRecord).reduce((columns, currentKey: string) => {
    return {
      ...columns,
      [currentKey]: filterRecord[currentKey]
        .find((filterSet: FluidFilterSet) => {
          return (
            filterSet.filterType === FluidFilterType.COLUMN ||
            filterSet.filterType === FluidFilterType.CUSTOM
          );
        })
        ?.filters.map((filter: FluidFilter) => filter.id),
    };
  }, {});
}

/**
 * If the table is stateful, persist the applied and selected filter state
 * to local storage for retrieval later.
 * @param host
 */
function persistFilterState(host: Partial<FluidTable>) {
  const { _appliedFilters, _rangeFilter, tableId, statefulOptions } = host;

  const appliedFiltersByColumn = collectRangeFiltersByColumn(
    collectFilterIdsFromRecord(_appliedFilters),
    _rangeFilter
  );

  if (statefulOptions?.filters?.enabled) {
    FluidBrowserStorage.set(
      TABLE_FILTER_STATE(tableId),
      {
        appliedFiltersByColumn,
      },
      statefulOptions?.filters?.strategy
    );
  } else {
    FluidBrowserStorage.clear(
      TABLE_FILTER_STATE(tableId),
      host.statefulOptions?.filters?.strategy
    );
  }
}

export function collectDateRangeFilters(
  rangeFilters: Record<string, any>
): FluidFilterSet[] {
  return Object.keys(rangeFilters).reduce(
    (filterSets: FluidFilterSet[], dataPath: string) => {
      return [
        ...filterSets,
        {
          type: 'date',
          dataPath,
          id: `${dataPath}_range_filter`,
          filterType: FluidFilterType.RANGE,
          filters: [
            {
              label: '',
              id: generateUniqueId(),
              predicate: (dataItem: FluidRowData) =>
                isDataItemDateInRange(dataItem, dataPath, rangeFilters),
            },
          ],
        },
      ];
    },
    []
  );
}

export function isDataItemDateInRange(
  dataItem: FluidRowData,
  dataPath: string,
  rangeFilters: Record<string, any>
): boolean {
  const valueAtPath = getAtPath(dataItem, dataPath);
  let dateToCheck = parseISO(valueAtPath);
  if (isNaN(dateToCheck.getTime())) {
    dateToCheck = new Date(valueAtPath);
  }
  const start = parseISO(rangeFilters[dataPath].from);
  const end = parseISO(rangeFilters[dataPath].to);
  Utils.debug('[TABLE] isDataItemDateInRange', {
    dateToCheck,
    valueAtPath,
    start,
    end,
    dataPath,
    dataItem,
    value: getAtPath(dataItem, dataPath),
  });
  return isWithinInterval(dateToCheck, { start, end });
}

/**
 * Updates the filtered rows store on the host
 * @param host
 * @param passingDataItems
 */
function setFilteredRows(
  host: Partial<FluidTable>,
  passingDataItems: FluidTableRowData<any>[]
): void {
  const passingDataItemsSet = new Set(passingDataItems);
  host._filteredRows = host._dataSource.filter(
    (dataItem) => !passingDataItemsSet.has(dataItem)
  );
}

/**
 * Combines the selected column filters and selected custom filters,
 * and returns an array of dataItems that pass all the filters.
 * @param columnFilters
 * @param customFilters
 * @param searchFilters
 * @param dateRangeFilters
 * @param dataItems
 */
function runFilters<T = FluidRowData>(
  columnFilters: FluidFilterSet[],
  customFilters: FluidFilterSet[],
  searchFilters: FluidFilterSet[],
  dateRangeFilters: FluidFilterSet[],
  dataItems: T[]
) {
  return dataItems.filter((dataItem) => {
    return [
      ...columnFilters,
      ...customFilters,
      ...searchFilters,
      ...dateRangeFilters,
    ].every((filterSet: FluidFilterSet) => {
      return filterSet.filters.some((filter: FluidFilter) => {
        return filter.predicate(dataItem);
      });
    });
  });
}

/**
 * Given a set of selected filters, and a type, extracts the filters by the
 * given type.
 * @param selectedFilters
 * @param type
 */
export const extractFiltersByType = (
  selectedFilters: Record<string, any>,
  type: FluidFilterType
): FluidFilterSet[] => {
  return Object.keys(selectedFilters).reduce((columnFilters, dataPath) => {
    return [
      ...columnFilters,
      ...selectedFilters[dataPath].filter(
        (filterSet: FluidFilterSet) => filterSet.filterType === type
      ),
    ];
  }, []);
};

/**
 * Apply filters, paginate and regroup data.
 * @param host
 */
export const refreshTableAfterFilters = (host: Partial<FluidTable>) => {
  const start = performance.now();
  applyPagination(host);
  setGroupedDataSource(host);
  host.setPaginationState(0).then();
  handleFilterEvents(host);
  Utils.logPerformance('refreshTableAfterFilters', start);
};

/**
 * Groups the data items and set the host _groupedDataSource memory
 * store
 * @param host
 * @param dataSource
 */
export function setGroupedDataSource(
  host: Partial<FluidTable>,
  dataSource: FluidRowData[] = host._dataSource
): void {
  host._groupedDataSource = groupDataSource(host._headers, dataSource);
  host._dataRefreshing = false;
}

/**
 * Updates the filter meta items (visible, filtered, expanded) on each item
 * in the data source based on whether it exists in the _filteredRows array
 * or not.
 * @param host
 */
export function updateDataSourceFilterMeta(host: Partial<FluidTable>) {
  const start = performance.now();
  const filteredSet = new Set(host._filteredRows.map((r) => r[ID]));
  host._dataSource = host._dataSource.map((dataItem) => {
    const visible = !filteredSet.has(dataItem[ID]);
    const isCurrentlyExpanded = !!dataItem[FORM_EXPANDED];
    return {
      ...dataItem,
      [VISIBLE]: visible,
      [FILTERED]: !visible,
      [FORM_EXPANDED]: visible ? isCurrentlyExpanded : false,
    };
  });
  if (host.helpers.search.isSearchApplied()) {
    applySearch(host, host.searchOptions?.clearOnFilter);
  }
  Utils.logPerformance('updateDataSourceFilterMeta', start);
}

/**
 * Executes a search on the data source host element, clearing
 * after filters if necessary
 * @param host
 * @param clearSearchTerm
 */
function applySearch(
  host: Partial<FluidTable>,
  clearSearchTerm: boolean
): void {
  const start = performance.now();
  if (clearSearchTerm) {
    host.helpers.search.setSearchTerm('');
  }

  if (host.helpers.search.hasSearchTerm()) {
    host.helpers.search.searchTable();
  }
  Utils.logPerformance('applySearch', start);
}

/**
 * Creates a blank value filter toggle
 * @param header
 * @param appliedFilters
 */
export const blankValue = (
  header: FluidTableHeader,
  appliedFilters: FluidFilterSet[],
  persistedFilters
) => {
  const filterToggleId = FILTER_TOGGLE_ID(
    header.dataPath,
    BLANK_FILTER_ID_VALUE
  );
  return {
    label: BLANK_FILTER_LABEL,
    id: filterToggleId,
    controlName: filterToggleId,
    selected: !isFilterApplied(
      appliedFilters,
      filterToggleId,
      persistedFilters
    ),
  };
};

/**
 * Checks if the given filterId exists in the array of applied filters. If it does,
 * return true, as the filter is being applied (and therefore should not be checked
 * in the filter toggle group)
 * @param appliedFilters
 * @param filterId
 * @param persistedFilters
 */
export const isFilterApplied = (
  appliedFilters: FluidFilterSet[],
  filterId: string,
  persistedFilters?: string[]
): boolean => {
  if (persistedFilters?.length > 0) {
    return !!persistedFilters?.find(
      (persistedFilter) => persistedFilter === filterId
    );
  }

  if (!appliedFilters?.length) return false;

  return !!appliedFilters.find((filterSet) => {
    return !!filterSet?.filters?.find((filter) => filter.id === filterId);
  });
};

/**
 * Finds a custom filter by its label.
 * @param header
 * @param label
 */
export const findCustomFilter = (header: FluidTableHeader, label: string) => {
  const flattened = header.customFilters.reduce(
    (allFilters: FluidCustomFilterFn[], current: FluidCustomFilterSet) => {
      return [...allFilters, ...current.filters];
    },
    []
  );
  return flattened.find((f) => f.label === label);
};

/**
 * Gets the filter type from the filter ID (column/custom/etc.)
 * @param filterId
 */
export const getFilterType = (filterId: string) =>
  filterId.split(FILTER_TYPE_DELIM)[1];

/**
 * Returns true if the event menu section is a basic column filter
 * @param menuSection
 */
export const isColumnFilter = (menuSection: FluidContextMenuSection) => {
  return getFilterType(menuSection.id) === FluidFilterType.COLUMN;
};

/**
 * Returns true if the given menu section has at least one toggle in the given
 * selected state.
 * @param menuSection
 * @param selected
 */
export const hasTogglesSelected = (
  menuSection: FluidContextMenuSection,
  selected: boolean
) => {
  return (
    menuSection.toggles.filter((toggle) => toggle.selected === selected)
      .length > 0
  );
};

/**
 * Removes filter controls from the parsed menu sections.
 * @param menuSections
 */
function removeNonFilterSections(menuSections: FluidContextMenuSection[]) {
  return menuSections.filter(
    (menuSection: FluidContextMenuSection) =>
      menuSection.type !== FluidContextMenuItemType.LINK
  );
}

/**
 * Extraction utility to find COLUMN filters from menu section array.
 * @param menuSections
 */
function getColumnFilters(menuSections: FluidContextMenuSection[]) {
  return menuSections.filter(
    (ms: FluidContextMenuSection) =>
      isColumnFilter(ms) && !isSearch(ms) && !isDateRange(ms)
  );
}

/**
 * Extraction utility to find CUSTOM filters from menu section array.
 * @param menuSections
 */
function getCustomFilters(menuSections: FluidContextMenuSection[]) {
  return menuSections.filter(
    (ms: FluidContextMenuSection) =>
      !isColumnFilter(ms) && !isSearch(ms) && !isDateRange(ms)
  );
}

/**
 * Extraction utility to find SEARCH filters from menu section array.
 * @param menuSections
 */
function getSearchFilter(menuSections: FluidContextMenuSection[]) {
  return menuSections.filter((ms: FluidContextMenuSection) => isSearch(ms));
}

/**
 * Shorthand filter aggregator
 */
const TableFilters = {
  COLUMN: (menuSections: FluidContextMenuSection[]) =>
    getColumnFilters(menuSections),
  CUSTOM: (menuSections: FluidContextMenuSection[]) =>
    getCustomFilters(menuSections),
  SEARCH: (menuSections: FluidContextMenuSection[]) =>
    getSearchFilter(menuSections),
};

/**
 * Updates the _selectedFilters memory store at the given headers dataPath
 * with those filters that are selected.
 * @param header
 * @param menuSections
 * @param host
 */
export const updateSelectedFilters = (
  header: FluidTableHeader,
  menuSections: FluidContextMenuSection[],
  host: FluidTable
): void => {
  const start = performance.now();
  const filterSections = removeNonFilterSections(menuSections);
  host._selectedFilters[header.dataPath] = [
    ...TableFilters.COLUMN(filterSections).map((ms) =>
      FILTER_SET(FluidFilterType.COLUMN, true, ms, header)
    ),
    ...TableFilters.CUSTOM(filterSections).map((ms) =>
      FILTER_SET(FluidFilterType.CUSTOM, true, ms, header)
    ),
    ...TableFilters.SEARCH(filterSections).map((ms) =>
      FILTER_SET(FluidFilterType.CONTAINS, true, ms, header)
    ),
  ];
  Utils.logPerformance('updateSelectedFilters', start);
};

/**
 * Updates the _appliedFilters memory store at the given headers dataPath
 * with those filters that are not selected
 * @param header
 * @param menuSections
 * @param host
 */
export const updateAppliedFilters = (
  header: FluidTableHeader,
  menuSections: FluidContextMenuSection[],
  host: FluidTable
): void => {
  const start = performance.now();
  const filterSections = removeNonFilterSections(menuSections);
  host._appliedFilters[header.dataPath] = [
    ...filterSections
      .filter(
        (ms: FluidContextMenuSection) =>
          isColumnFilter(ms) && hasTogglesSelected(ms, false) && !isSearch(ms)
      )
      .map((ms) => FILTER_SET(FluidFilterType.COLUMN, false, ms, header)),
    ...filterSections
      .filter(
        (ms) =>
          !isColumnFilter(ms) && hasTogglesSelected(ms, false) && !isSearch(ms)
      )
      .map((ms) => FILTER_SET(FluidFilterType.CUSTOM, false, ms, header)),
    ...filterSections
      .filter(
        (ms) =>
          !isColumnFilter(ms) && hasTogglesSelected(ms, false) && isSearch(ms)
      )
      .map((ms) => FILTER_SET(FluidFilterType.CONTAINS, false, ms, header)),
  ];
  Utils.logPerformance('updateAppliedFilters', start);
};

export const isSearch = (menuSection: FluidContextMenuSection) =>
  menuSection.type === FluidContextMenuItemType.SLOT &&
  menuSection.slotName?.includes(SEARCH_SLOT_SUFFIX);

export const isDateRange = (menuSection: FluidContextMenuSection) =>
  menuSection.type === FluidContextMenuItemType.SLOT &&
  menuSection.slotName?.includes(DATE_RANGE_SLOT_SUFFIX);

/**
 * Returns true if any filters are applied, which allows us to determine whether to
 * show certain indicators, etc.
 * @param host
 */
export const filtersAreApplied = (host: Partial<FluidTable>): boolean => {
  return (
    Object.keys(host._appliedFilters).some(
      (dataPath) => host._appliedFilters[dataPath]?.length > 0
    ) ||
    Object.keys(host._rangeFilter)?.length > 0 ||
    host.helpers.search.isSearchApplied()
  );
};

/**
 * Returns a list of all visible dataItems
 * @param groupedDataSource
 */
export const getVisibleDataItems = (
  groupedDataSource: FluidTableGroupedDataset[]
) => {
  return groupedDataSource
    .reduce(
      (dataItems: FluidRowData[], currentDataset: FluidTableGroupedDataset) => {
        return [...dataItems, ...currentDataset.groupedData];
      },
      []
    )
    .filter((dataItem) => dataItem[VISIBLE]);
};

/**
 * Creates a filter set based on unique values in the column
 * @param header
 * @param dataItems
 * @param appliedFilters
 * @param persistedFilters
 */
export const createColumnFilters = (
  header: FluidTableHeader,
  dataItems: FluidRowData[],
  appliedFilters: FluidFilterSet[],
  persistedFilters?: string[]
) => {
  const {
    dataPath,
    columnFilterFormat,
    columnFilterCompareFormat,
    normaliseColumnFilterLabels,
  } = header;

  /**
   * Create the filter set for the given header by finding all
   * distinct values and using the columnFilterCompareFormat to
   * create the normalised value if supplied.
   */
  const filterSet = createFilterSet(
    dataItems,
    [dataPath],
    columnFilterCompareFormat
  );

  /**
   * Maps over the filters set for the given header, and creates a toggle for each entry.
   */
  return filterSet[dataPath].map((filterSetValue: FilterSetValue) => {
    return createFilterToggle(
      filterSetValue,
      columnFilterFormat,
      normaliseColumnFilterLabels,
      dataPath,
      appliedFilters,
      persistedFilters
    );
  });
};

/**
 * Creates the toggle configuration for the given filter value
 * @param filterSetValue
 * @param columnFilterFormat
 * @param normaliseColumnFilterLabels
 * @param dataPath
 * @param appliedFilters
 * @param persistedFilters
 */
function createFilterToggle(
  filterSetValue: FilterSetValue,
  columnFilterFormat: StringReturn<string>,
  normaliseColumnFilterLabels: boolean,
  dataPath: string,
  appliedFilters: FluidFilterSet[],
  persistedFilters: string[]
): FluidContextMenuToggle {
  const filterToggleId = FILTER_TOGGLE_ID(dataPath, filterSetValue.normalised);

  return {
    label: createFilterItemLabel(
      filterSetValue,
      columnFilterFormat,
      normaliseColumnFilterLabels
    ),
    id: filterToggleId,
    normalisedValue: filterSetValue.normalised,
    controlName: filterToggleId,
    selected: !isFilterApplied(
      appliedFilters,
      filterToggleId,
      persistedFilters
    ),
  };
}

/**
 * If header has a columnFilterFormat configured, use it to create the display label
 * for the filter, otherwise, if normaliseColumnFilterLabels is true, convert it to
 * title case, else return the raw value as the label.
 * @param filterSetValue
 * @param columnFilterFormat
 * @param normaliseColumnFilterLabels
 */
function createFilterItemLabel(
  filterSetValue: FilterSetValue,
  columnFilterFormat: (displayValue: string) => string,
  normaliseColumnFilterLabels: boolean
) {
  return columnFilterFormat
    ? columnFilterFormat(filterSetValue.display)
    : typeof filterSetValue.display === 'string' && normaliseColumnFilterLabels
    ? convertToTitleCase(filterSetValue.display)
    : filterSetValue.display;
}

/**
 * Creates a filter set from the given dataSource and the given paths
 * using an optional custom function to run before normalising.
 * @param dataSource
 * @param paths
 * @param columnFilterCompareFormat
 */
export const createFilterSet = (
  dataSource: any[],
  paths: string[],
  columnFilterCompareFormat?: (dataValue: any) => string
): Record<string, FilterSetValue[]> => {
  return paths.reduce(
    (filterSet: Record<string, FilterSetValue[]>, path: string) => {
      return {
        ...filterSet,
        [path]: collectDistinctValues(
          dataSource,
          path,
          columnFilterCompareFormat
        ),
      };
    },
    {}
  );
};

/**
 * Pulls distinct values from a dataSource at the given data path into an array
 * of FilterSetValues - includes a normalised version (no spaces, all lowercase) for
 * comparison and a value for displaying in any filter style and function can be supplied to run on the data before
 * normalising.
 * @param dataSource
 * @param dataPath
 * @param columnFilterCompareFormat
 */
export const collectDistinctValues = (
  dataSource: any[],
  dataPath: string,
  columnFilterCompareFormat?: (dataValue: any) => string
): FilterSetValue[] => {
  return sortAlpha(
    dataSource.reduce((distinctValues: any[], current: DatasourceItem) => {
      const dataItem = getAtPath(current, dataPath);
      if (dataItem) {
        if (Array.isArray(dataItem)) {
          dataItem.forEach((dataItemElement) => {
            return normaliseAndAddDistinctValue(
              dataItemElement,
              distinctValues,
              columnFilterCompareFormat
            );
          });
        } else {
          return normaliseAndAddDistinctValue(
            dataItem,
            distinctValues,
            columnFilterCompareFormat
          );
        }
      }
      return distinctValues;
    }, []),
    'normalised'
  );
};

/**
 * Normalises the value passed in and adds it to the array passed in if the value is distinct. A custom function can be
 * passed in to be applied to the value before it is normalised.
 * @param value
 * @param distinctValues
 * @param columnFilterCompareFormat
 */
function normaliseAndAddDistinctValue(
  value: any,
  distinctValues: FluidContextMenuToggle[],
  columnFilterCompareFormat?: (dataValue: any) => string
) {
  const normalised = columnFilterCompareFormat
    ? normalise(columnFilterCompareFormat(value))
    : normalise(value);
  return addDistinct(
    distinctValues,
    {
      normalised,
      display: value,
    } as FilterSetValue,
    (distinctValue: FluidContextMenuToggle) =>
      distinctValue.normalised === normalised
  );
}

/**
 * Creates the filter menu sections for the given header, taking into account filters that
 * are already applied when determining if the initial value is selected or not
 * @param header
 * @param appliedFilters
 * @param dataItems
 * @param compactFilters
 * @param serverMode
 * @param fluidTranslate
 * @param i18nStore
 * @param persistedFilters
 */
export const createFilterMenuSections = (
  header: FluidTableHeader,
  appliedFilters: FluidFilterSet[],
  dataItems: FluidRowData[],
  compactFilters = false,
  serverMode = false,
  fluidTranslate?: FluidTableTranslationOptions,
  i18nStore?: FluidI18nStore,
  persistedFilters?: string[]
) => {
  const { customFilters, dataPath } = header;
  if (customFilters) {
    return createCustomMenuSections(
      customFilters,
      appliedFilters,
      dataPath,
      compactFilters,
      persistedFilters
    );
  } else {
    return !serverMode
      ? [
          createColumnFilterMenuSection(
            header,
            dataItems,
            createColumnFilters(
              header,
              dataItems,
              appliedFilters,
              persistedFilters
            ),
            appliedFilters,
            compactFilters,
            fluidTranslate,
            i18nStore,
            persistedFilters
          ),
        ]
      : [];
  }
};

/**
 * Iterates the custom filters given and creates a context menu
 * section for each, containing title, id, sectionHeight and a
 * toggle group made from the filters in each custom filter set.
 * @param customFilters
 * @param appliedFilters
 * @param dataPath
 * @param compactFilters
 *
 * @param persistedFilters
 * @return FluidContextMenuSection[]
 */
function createCustomMenuSections(
  customFilters: FluidCustomFilterSet[],
  appliedFilters: FluidFilterSet[],
  dataPath: string,
  compactFilters: boolean,
  persistedFilters: string[]
): FluidContextMenuSection[] {
  return [
    ...customFilters.map((customFilter: FluidCustomFilterSet) => {
      const id = FILTER_SET_ID(dataPath, customFilter.type);
      const { title, sectionHeight, filters } = customFilter;
      const filterSetValue = (
        customFilter: FluidCustomFilterFn
      ): FilterSetValue => ({
        normalised: normalise(customFilter.label),
        display: customFilter.label,
      });
      return {
        title,
        id,
        type: FluidContextMenuItemType.TOGGLE_GROUP,
        sectionHeight:
          sectionHeight || (compactFilters && DEFAULT_COMPACT_SECTION_HEIGHT),
        toggles: filters.map((customFilter: FluidCustomFilterFn) =>
          createFilterToggle(
            filterSetValue(customFilter),
            undefined,
            false,
            dataPath,
            appliedFilters,
            persistedFilters
          )
        ),
      };
    }),
  ];
}

export const createColumnFilterMenuSection = (
  header: FluidTableHeader,
  dataItems: FluidRowData[],
  columnFilters: any,
  appliedFilters: FluidFilterSet[],
  compactFilters = false,
  fluidTranslate?: FluidTableTranslationOptions,
  i18nStore?: FluidI18nStore,
  persistedFilters?: any
) =>
  ({
    type: FluidContextMenuItemType.TOGGLE_GROUP,
    sectionHeight: compactFilters && DEFAULT_COMPACT_SECTION_HEIGHT,
    id: FILTER_SET_ID(header.dataPath),
    title:
      i18nStore?.translate(fluidTranslate?.filters?.columnFilters?.label) ||
      COLUMN_FILTER_TITLE_DEFAULT,
    toggles:
      filterEmpty(dataItems, header.dataPath).length > 0
        ? [
            blankValue(header, appliedFilters, persistedFilters),
            ...columnFilters,
          ]
        : columnFilters,
  } as FluidContextMenuSection);

/**
 * Emits whenever a filter is applied to inform the consuming component
 * of what filters have been applied, and what effect that had on the
 * datasource.
 * @param host
 */
export const handleFilterEvents = (host: Partial<FluidTable>) => {
  const filterPayload = {
    appliedFilters: host._appliedFilters,
    filteredRows: host._filteredRows,
  };
  if (
    appliedFiltersHaveChanged(host, filterPayload) &&
    (shouldEmitFilterEvent(host, filterPayload) || host.emitOnEmptyFilter)
  ) {
    emitFilterEvent(host, filterPayload);
  } else {
    emitClearFilterEvent(host);
  }
};

/**
 * Emit the filtersCleared event and log to data layer.
 * @param host
 */
export function emitClearFilterEvent(host: Partial<FluidTable>) {
  if (host.tableHasInitialisedAndRendered && host._filteredRows?.length === 0) {
    host.filtersCleared.emit(true);
    sendEvent(host, host.parentLayerId, 'filtersCleared', true);
  }
}

/**
 * Emit the tableFiltered event and log to data layer.
 * @param host
 * @param filterPayload
 */
export function emitFilterEvent(
  host: Partial<FluidTable>,
  filterPayload: FilterEventPayload
) {
  host.tableFiltered.emit(filterPayload);
  sendEvent(host, host.parentLayerId, 'tableFiltered', filterPayload);
  host._lastFilterPayload = filterPayload;
}

/**
 * Check if filters are applied before emitting a tableFiltered event. If not,
 * emit the filtersCleared event, so long as the table has already initialized.
 * @param host
 * @param filterPayload
 */
export function shouldEmitFilterEvent(
  host: Partial<FluidTable>,
  filterPayload: FilterEventPayload
) {
  if (!filtersAreApplied(host) && host.tableHasInitialisedAndRendered) {
    emitClearFilterEvent(host);
  }

  return (
    (filtersAreApplied(host) || host._filteredRows?.length > 0) &&
    appliedFiltersHaveChanged(host, filterPayload)
  );
}

/**
 * Checks if the last emitted filter payload matches the one we are about to emit, so we can cancel the
 * filter event to prevent multiple repeated events.
 * @param host
 * @param filterPayload
 */
export function appliedFiltersHaveChanged(
  host: Partial<FluidTable>,
  filterPayload: FilterEventPayload
) {
  return !Utils.isEqual(host._lastFilterPayload, filterPayload);
}
